import { FC } from "react";
import { Box, Card, Typography, Skeleton, Grid } from "@mui/material";
import { ResponsiveContainer, AreaChart, Area } from "recharts";
import { MonitoringKPI } from "app/data/types";
import { EngineOperatingIcon, TotalOperatingIcon } from "assets/icons";

interface Props {
  kpis: MonitoringKPI[];
  loading: boolean;
}

const formatKpiValue = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
    maximumFractionDigits: 2
  }).format(value);
};

const getKpiIcon = (title: string) => {
  const normalized = title.replace(/[_\s-]/g, "").toLowerCase();
  if (normalized.includes("engineoperating")) {
    return EngineOperatingIcon;
  }
  return TotalOperatingIcon;
};

const KPITilesRow: FC<Props> = ({ kpis, loading }) => {
  const hasKpiData = kpis.some((kpi) => (kpi.sparklineData?.length ?? 0) > 0);

  if (loading) {
    return (
      <Grid container spacing={3} mb={4}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} md={4} key={i}>
            <Skeleton variant="rounded" height={100} width="100%" />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (!hasKpiData) {
    return (
      <Grid container spacing={3} mb={4}>
        {[1, 2, 3].map((i) => (
          <Grid item xs={12} md={4} key={`empty-kpi-${i}`}>
            <Card
              variant="outlined"
              sx={{
                p: 2,
                position: "relative",
                height: "100%",
                minHeight: 112,
                borderRadius: "8px",
                borderColor: "#D5D9E8",
                overflow: "hidden"
              }}
            >
              <Box sx={{ filter: "blur(2px)", opacity: 0.55 }}>
                <Typography
                  variant="overline"
                  sx={{
                    color: "#646D7B",
                    fontWeight: 600,
                    display: "block",
                    mb: 0.5
                  }}
                >
                  KPI
                </Typography>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 700, color: "#1D1F2B" }}
                >
                  --
                </Typography>
                <Box sx={{ width: 130, height: 40, mt: 1 }}>
                  <svg
                    width="100%"
                    height="100%"
                    viewBox="0 0 130 40"
                    preserveAspectRatio="none"
                  >
                    <polyline
                      points="0,34 20,28 45,26 68,22 92,18 110,15 130,10"
                      fill="none"
                      stroke="#8A56FA"
                      strokeWidth="2"
                    />
                  </svg>
                </Box>
              </Box>
              <Typography
                variant="body2"
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#646D7B",
                  fontWeight: 500,
                  textAlign: "center",
                  px: 2
                }}
              >
                No data available for this period.
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={3} mb={4}>
      {kpis.map((kpi) => (
        <Grid item xs={12} md={4} key={kpi.id}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              height: "100%",
              minHeight: 112,
              borderRadius: "8px",
              borderColor: "#D5D9E8"
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 0.5
                }}
              >
                {(() => {
                  const KpiIcon = getKpiIcon(kpi.title);
                  return (
                    <KpiIcon
                      style={{
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                        color: "#646D7B",
                        marginTop: 1
                      }}
                    />
                  );
                })()}
                <Typography
                  variant="overline"
                  sx={{
                    color: "#646D7B",
                    fontWeight: 600,
                    display: "block",
                    lineHeight: 1.2,
                    minHeight: 34
                  }}
                >
                  {kpi.title.toUpperCase()}
                </Typography>
              </Box>
              <Typography
                variant="h4"
                sx={{ fontWeight: 700, color: "#1D1F2B" }}
              >
                {kpi.valueLabel ??
                  `${formatKpiValue(kpi.currentValue)}${kpi.unit}`}
              </Typography>
            </Box>

            {/* Sparkline */}
            <Box sx={{ width: 130, height: 56 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={kpi.sparklineData}>
                  <defs>
                    <linearGradient
                      id={`kpi-gradient-${kpi.id}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor="#8A56FA"
                        stopOpacity={0.28}
                      />
                      <stop offset="95%" stopColor="#8A56FA" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="value"
                    stroke="#8A56FA"
                    strokeWidth={2}
                    fill={`url(#kpi-gradient-${kpi.id})`}
                    fillOpacity={1}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
};

export default KPITilesRow;
